import { Flex } from '@chakra-ui/react';
import { Button, FormFeedback, TextArea, useToast } from '@morressier/alembic';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import { ISubmissionWFState } from '@publishing-engine/domain/types/workflow-phases';
import { FormProvider, useForm } from 'react-hook-form';
import { useRecommendation } from 'src/api/commands';
import { FieldLabel } from 'src/components/Field';
import { FormFieldMultipleFileUpload } from 'src/components/FormField';
import { RadioGroup } from 'src/components/RadioGroup';
import { useCOCContextData } from 'src/contexts/COCDataProvider';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { getFileUploadArrayFFDConfigs } from 'src/utils/formHelpers';
import { cocAllowedStates } from './RecommendationAction';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';
import { useEffect } from 'react';

export type IRecommendationForm = {
  decision: string;
  commentForAuthor: string;
  commentForEditor: string;
  attachments: ISubmissionFile[];
};

const getDefaultRecommendationFormValues = (): IRecommendationForm => ({
  decision: '',
  commentForAuthor: '',
  commentForEditor: '',
  attachments: []
});

const RecommendationForm = () => {
  const { submissionContext } = useSubmissionContextData();
  const { organizationId, workflowResponse } = useSubmissionData();
  const {
    currentUserRoles,
    currentUserRole,
    currentUserID,
    isCurrentOwner,
    recommedationSummary
  } = useCOCContextData();
  const { submissionId, submissionContextId } = workflowResponse;
  const recommendationWorkflowData =
    recommedationSummary?.recommendationWorkflowData;
  const workflowRole = recommendationWorkflowData?.currentRole;
  const canActInWorkflowRole = workflowRole
    ? currentUserRoles.includes(workflowRole)
    : false;
  const actingRole = canActInWorkflowRole
    ? (workflowRole as string)
    : currentUserRole ?? '';
  const Config = submissionContext?.config.cocConfig?.find(
    user => user.role === actingRole
  )?.decision;
  const methods = useForm<IRecommendationForm>({
    defaultValues: getDefaultRecommendationFormValues()
  });
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors }
  } = methods;
  const toast = useToast();
  const submitCOCRecommendationMutation = useRecommendation({
    submissionContextId,
    submissionId,
    organizationId,
    cocEditorId: currentUserID ?? '',
    role: actingRole
  });
  const submissionCurrentState = workflowResponse.workflowData
    .currentState as ISubmissionWFState;
  const isCocAllowedState = cocAllowedStates.includes(submissionCurrentState);
  const isCurrentUserAwaiting =
    recommedationSummary?.awaitingUserIds?.includes(currentUserID ?? '') ??
    false;
  const onSubmitRecommendation = async (data: IRecommendationForm) => {
    const res = await submitCOCRecommendationMutation.mutateAsync(data);

    if (res.isSuccess) {
      reset(getDefaultRecommendationFormValues());
      return toast({
        variant: 'success',
        title: `Your Recommendation is submitted`,
        isClosable: true
      });
    }

    return toast({
      variant: 'error',
      title: res.message?.error ? res.message?.error : res?.message,
      isClosable: true
    });
  };
  const files = watch('attachments') || [];
  const isDisabled = !(
    canActInWorkflowRole &&
    recommendationWorkflowData?.currentState === 'awaitingRecommendation'
  );
  useEffect(() => {
    reset(getDefaultRecommendationFormValues());
  }, [recommedationSummary, reset]);
  return (
    canActInWorkflowRole &&
    isCocAllowedState &&
    isCurrentUserAwaiting && (
      <Flex
        flexDirection="column"
        width="100%"
        backgroundColor="surfacePrimaryFill"
        padding={4}
      >
        <>
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(
                onSubmitRecommendation,
                scrollToFirstError
              )}
            >
              <Flex
                flexDirection="column"
                gap={4}
                backgroundColor="surfacePrimaryFill"
              >
                <Flex gap={2} flexDirection="column">
                  <Flex flexDirection="column" gap={2}>
                    <FieldLabel
                      label="Recommendation"
                      subText={''}
                      isRequired={true}
                    />
                    {Config && (
                      <RadioGroup
                        name={'decision'}
                        control={control}
                        required={true}
                        options={Config.map(option => {
                          return {
                            value: option.value,
                            children: option.label
                          };
                        })}
                      />
                    )}
                    {errors.decision && (
                      <FormFeedback variant="error">
                        {errors.decision.message}
                      </FormFeedback>
                    )}
                  </Flex>
                  <Flex flexDirection="column" gap={2}>
                    <FieldLabel
                      label="Write a comment for Author(s)"
                      subText={''}
                      isRequired={false}
                    />
                    <TextArea
                      minHeight={100}
                      maxHeight={600}
                      value={watch('commentForAuthor')}
                      {...register('commentForAuthor')}
                      placeholder="Write a comment for author(s)"
                    />
                  </Flex>
                  <Flex flexDirection="column" gap={2}>
                    <FieldLabel
                      label={'Write a comment for Staff'}
                      subText={''}
                      isRequired={false}
                    />
                    <TextArea
                      minHeight={100}
                      maxHeight={600}
                      value={watch('commentForEditor')}
                      {...register('commentForEditor')}
                      placeholder={'Write a comment for Staff'}
                    />
                  </Flex>
                  <Flex flexDirection="column" gap={2}>
                    <FormFieldMultipleFileUpload
                      config={getFileUploadArrayFFDConfigs('attachments')}
                      submissionId={submissionId}
                      submissionContextId={submissionContextId}
                    />
                  </Flex>
                </Flex>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={
                    isCurrentOwner ||
                    submitCOCRecommendationMutation.isLoading ||
                    files.some(file => file.isUploading) ||
                    isDisabled
                  }
                >
                  Submit Recommendation
                </Button>
              </Flex>
            </form>
          </FormProvider>
        </>
      </Flex>
    )
  );
};

export default RecommendationForm;
